import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, TrendingUp, Package, AlertTriangle, DollarSign, Wrench, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function StatsPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/reports/stats/overview');
        setStats(res.data);
      } catch { toast.error(t('errors.loadingError')); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, [t]);

  if (loading) {
    return (
      <div data-testid="stats-page" className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  const kpis = [
    { label: t('stats.activeJobs'), value: stats.active_jobs, icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: t('stats.completedJobs'), value: stats.completed_jobs, icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: t('stats.totalInvoiced'), value: `$${(stats.total_invoiced || stats.revenue_collected || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
    { label: t('stats.pendingInvoices'), value: stats.pending_invoices, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: t('stats.inventoryValue'), value: `$${(stats.inventory_value || 0).toLocaleString()}`, icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: t('stats.lowStockItems'), value: stats.low_stock_items, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  const maxJobsByType = Math.max(...(stats.jobs_by_type || []).map(j => j.count), 1);
  const maxJobsByStatus = Math.max(...(stats.jobs_by_status || []).map(j => j.count), 1);

  return (
    <div data-testid="stats-page" className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
          <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('stats.title')}
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('stats.subtitle')}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <Card key={i} className="bg-white border-gray-100 hover:shadow-md transition-shadow">
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-medium">{kpi.label}</p>
                    <p className={`text-lg sm:text-2xl font-bold mt-1 sm:mt-2 ${kpi.color}`}>{kpi.value}</p>
                  </div>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${kpi.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="bg-white border-gray-100">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">{t('stats.jobsByType')}</h3>
            <div className="space-y-3">
              {(stats.jobs_by_type || []).map((item, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 font-medium">{t(`stats.jobTypes.${item.type}`, { defaultValue: item.type })}</span>
                    <span className="text-gray-500 font-semibold">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#003366] rounded-full transition-all duration-500" style={{ width: `${(item.count / maxJobsByType) * 100}%` }} />
                  </div>
                </div>
              ))}
              {(stats.jobs_by_type || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t('common.noResults')}</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-100">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">{t('stats.jobsByStatus')}</h3>
            <div className="space-y-3">
              {(stats.jobs_by_status || []).map((item, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 font-medium">{t(`stats.jobStatuses.${item.status}`, { defaultValue: item.status })}</span>
                    <span className="text-gray-500 font-semibold">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${(item.count / maxJobsByStatus) * 100}%`, backgroundColor: ({ scheduled: '#6366f1', in_progress: '#3b82f6', completed: '#22c55e', cancelled: '#9ca3af', on_hold: '#eab308', invoiced: '#14b8a6' })[item.status] || '#64748b' }} />
                  </div>
                </div>
              ))}
              {(stats.jobs_by_status || []).length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t('common.noResults')}</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-100 lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> {t('reports.salesReport')}
            </h3>
            {(stats.monthly_revenue || []).length > 0 ? (
              <div className="flex items-end gap-2 h-40">
                {(stats.monthly_revenue || []).reverse().map((m, i) => {
                  const maxRev = Math.max(...(stats.monthly_revenue || []).map(x => x.total), 1);
                  const height = (m.total / maxRev) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-500 font-medium">${(m.total / 1000).toFixed(1)}k</span>
                      <div className="w-full bg-gray-100 rounded-t-md relative" style={{ height: '120px' }}>
                        <div className="absolute bottom-0 w-full bg-gradient-to-t from-[#003366] to-[#3399FF] rounded-t-md transition-all duration-500" style={{ height: `${height}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-400">{m.month?.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">{t('common.noResults')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
